import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { safeLocalStorage } from '../lib/safeStorage';

export default function SharePage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [copyLabel, setCopyLabel] = useState('Or copy link');
  const [copyHighlight, setCopyHighlight] = useState(false);

  const partnerBToken = sessionId ? safeLocalStorage.getItem(`quiz_token_b_${sessionId}`) : null;
  const shareUrl = sessionId && partnerBToken
    ? `${window.location.origin.replace(/\/+$/, '')}/quiz/${sessionId}/B?token=${partnerBToken}`
    : '';

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = shareUrl;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    setCopyLabel('COPIED!');
    setCopyHighlight(true);
    setTimeout(() => {
      setCopyLabel('Or copy link');
      setCopyHighlight(false);
    }, 2000);
  }, [shareUrl]);

  async function shareLink() {
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Take our quiz!',
          text: 'I started a quiz for us! Take it here:',
          url: shareUrl,
        });
      } else {
        copyToClipboard();
      }
    } catch {
      // Share dismissed
    }
  }

  function handleStartQuiz() {
    if (!sessionId) return;
    navigate(`/quiz/${sessionId}/A`);
  }

  return (
    <AppShell showBack backTo="/" title="Share Quiz">
      <div className="max-w-md mx-auto w-full px-6 pt-8 pb-12 flex flex-col items-center">

        {/* Invite Card */}
        <div className="w-full bg-purple-card border border-white/10 rounded-[2.5rem] p-8 shadow-2xl relative overflow-hidden mb-8">
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 blur-[80px] rounded-full" />

          <div className="relative z-10 text-center">
            <div className="text-5xl mb-6">💌</div>

            <h2 className="text-xl font-bold mb-2">Invite your partner</h2>
            <p className="text-sm text-slate-400 mb-8 leading-relaxed px-2">
              Send them this private link so they can answer their questions from their own device.
            </p>

            {/* Glass share button */}
            <button
              onClick={shareLink}
              className="w-full btn-glass text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-3 text-lg mb-6"
            >
              <span className="material-symbols-outlined text-white/80">share</span>
              Share link with partner
            </button>

            <div className="space-y-4">
              {/* Copy text link */}
              <button
                onClick={copyToClipboard}
                className="flex items-center justify-center gap-2 mx-auto text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:text-white transition-colors"
              >
                <span className="material-symbols-outlined text-sm">content_copy</span>
                <span className={copyHighlight ? 'text-primary' : ''}>{copyLabel}</span>
              </button>

              {/* Hint */}
              <div className="pt-4 border-t border-white/5">
                <p className="text-[11px] text-slate-500 italic">
                  You can also share later or at the end of the quiz.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Primary CTA */}
        <div className="w-full">
          <button
            onClick={handleStartQuiz}
            className="w-full gradient-cta text-white font-extrabold py-4 rounded-2xl text-lg shadow-xl flex items-center justify-center gap-2"
          >
            Start my Quiz &rarr;
          </button>
        </div>

      </div>
    </AppShell>
  );
}
